import React from 'react';
import PropTypes from 'prop-types';

// MUI components
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import AccountBalanceTwoToneIcon from '@mui/icons-material/AccountBalanceTwoTone';
import EventAvailableTwoToneIcon from '@mui/icons-material/EventAvailableTwoTone';
import {Typography} from '@mui/material';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import Alert from '@mui/material/Alert';

// Custom components
import Disclosure from 'components/Disclosure';
import AccordionTable from 'components/accordion-table';
import FilteredRestrictions from './FilteredRestrictions';

// Constants and utils
import {REFERENCE_TYPES, RESTRICTION_TYPES, ALLOWANCES_TYPES} from 'constants.js';
import {
  computeRestrictions,
  computeRestrictionTypeText,
  constructIdentifiers,
  computeAllowanceTypeText,
  formatOrganizationTypes,
  formatDateTimeWithTimezone
} from './utils';

const ManagingOrganizations = ({managingOrganizations}) => {
  const hasManagingOrganizations = managingOrganizations?.length > 0;
  const hasExistingManagingOrganization = managingOrganizations?.filter((o) => o.exists).length > 0;

  return (
    <>
      <Typography variant="overline">Managing Organizations</Typography>

      {hasManagingOrganizations && !hasExistingManagingOrganization && (
        <Alert style={{marginTop: 0, marginBottom: 24}} severity="warning">
          Patient has no existing managing organizations.
        </Alert>
      )}

      {hasManagingOrganizations ? (
        <>
          {managingOrganizations
            .filter((o) => !o.exists)
            .map((organization, index) => (
              <React.Fragment key={index}>
                {organization.id && (
                  <Alert style={{marginTop: 0, marginBottom: 24}} severity="info">
                    Organization with reference &apos;{organization.id}&apos; does not exist.
                  </Alert>
                )}

                {organization.identifiers &&
                  organization.identifiers.map((identifier, innerIndex) => (
                    <Alert key={innerIndex} style={{marginTop: 0, marginBottom: 24}} severity="info">
                      Organization with identifier &apos;{identifier.system}|{identifier.value}&apos; does not exist.
                    </Alert>
                  ))}
              </React.Fragment>
            ))}

          {managingOrganizations
            .filter((o) => o.exists)
            .map((organization) => (
              <AccordionTable
                key={organization.id}
                accordionHeader={`${organization.name}`}
                tableData={constructIdentifiers(organization.identifiers)}
                fieldNames={[
                  {key: 'system', value: 'System'},
                  {key: 'value', value: 'Value'}
                ]}
              >
                <>
                  <List>
                    <ListItem style={{paddingLeft: 0}}>
                      <ListItemIcon>
                        <AccountBalanceTwoToneIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={organization.id} secondary="FHIR resource ID" />
                    </ListItem>

                    <ListItem style={{paddingLeft: 0}}>
                      <ListItemIcon>
                        <AccountTreeTwoToneIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={REFERENCE_TYPES[organization.referenceType]} secondary="Reference type" />
                    </ListItem>

                    <ListItem style={{paddingLeft: 0}}>
                      <ListItemIcon>
                        <InfoTwoToneIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={formatOrganizationTypes(organization.types)}
                        secondary="Organization type"
                      />
                    </ListItem>

                    {organization?.lastFullUploadDate && (
                      <ListItem style={{paddingLeft: 0}}>
                        <ListItemIcon>
                          <EventAvailableTwoToneIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={formatDateTimeWithTimezone(organization.lastFullUploadDate)}
                          secondary="Last full upload date"
                        />
                      </ListItem>
                    )}
                  </List>

                  <Disclosure
                    isRestricted={organization.isAccessRestricted}
                    text={computeRestrictionTypeText(
                      RESTRICTION_TYPES.accessRestriction,
                      organization.isAccessRestricted
                    )}
                  ></Disclosure>
                  <Disclosure
                    isRestricted={organization.isSendRestricted}
                    text={computeAllowanceTypeText(ALLOWANCES_TYPES.sendAllowance, !organization.isSendRestricted)}
                  ></Disclosure>

                  <FilteredRestrictions
                    restrictions={computeRestrictions(organization?.accessRestrictions, true)}
                    restrictionType={RESTRICTION_TYPES.accessRestriction}
                  ></FilteredRestrictions>

                  <FilteredRestrictions
                    restrictions={computeRestrictions(organization?.sendAllowances, true)}
                    restrictionType={ALLOWANCES_TYPES.sendAllowance}
                  ></FilteredRestrictions>

                  <FilteredRestrictions
                    restrictions={computeRestrictions(organization?.accessRestrictions, false)}
                    restrictionType={RESTRICTION_TYPES.accessRestriction}
                  ></FilteredRestrictions>

                  <FilteredRestrictions
                    restrictions={computeRestrictions(organization?.sendAllowances, false)}
                    restrictionType={ALLOWANCES_TYPES.sendAllowance}
                  ></FilteredRestrictions>

                  <Typography style={{margin: '8px 0'}}>Identifiers</Typography>
                </>
              </AccordionTable>
            ))}
        </>
      ) : (
        <Alert style={{marginTop: 0, marginBottom: 24}} severity="warning">
          Patient has no managing organization.
        </Alert>
      )}
    </>
  );
};

ManagingOrganizations.propTypes = {
  managingOrganizations: PropTypes.array
};

export default ManagingOrganizations;
